import { useState } from "react";
import { HelpCircle, Loader2 } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { helpApi } from "@/lib/api";
import DOMPurify from "dompurify";

/**
 * Contextual help icon that slides open a right-side drawer with a help article.
 * Props:
 *   featureTag  — matches the article's feature_tag in MongoDB
 *   portalType  — "customer" | "admin" | "both" (default: auto-detect not needed, pass explicitly)
 *   className   — extra classes for the icon button
 */
export function HelpIcon({ featureTag, portalType = "customer", className = "" }) {
  const [open, setOpen] = useState(false);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const handleOpen = async () => {
    setOpen(true);
    if (fetched) return;
    setLoading(true);
    try {
      const results = await helpApi.listArticles({
        portal_type: portalType,
        feature_tag: featureTag,
      });
      setArticle(Array.isArray(results) && results.length > 0 ? results[0] : null);
    } catch {
      setArticle(null);
    } finally {
      setLoading(false);
      setFetched(true);
    }
  };

  const safeHtml = article?.content ? DOMPurify.sanitize(article.content) : "";

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 transition-colors shrink-0 ${className}`}
        data-testid={`help-icon-${featureTag}`}
        title="Help"
        aria-label="Open help"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-[420px] sm:max-w-[520px] overflow-y-auto flex flex-col"
          data-testid={`help-drawer-${featureTag}`}
        >
          <SheetHeader className="pb-4 border-b border-slate-100">
            <SheetTitle className="text-slate-900 text-base leading-snug">
              {loading ? "Loading…" : article?.title || "Help"}
            </SheetTitle>
            {article?.section && (
              <p className="text-xs text-slate-400 mt-0.5">{article.section}</p>
            )}
          </SheetHeader>

          <div className="flex-1 mt-4 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : article ? (
              <div
                className="prose prose-sm max-w-none prose-headings:text-slate-800 prose-a:text-cyan-600 prose-code:bg-slate-100 prose-code:px-1 prose-code:py-0.5 prose-code:text-cyan-700 prose-pre:bg-slate-950 prose-pre:text-slate-200"
                dangerouslySetInnerHTML={{ __html: safeHtml }}
                data-testid={`help-drawer-content-${featureTag}`}
              />
            ) : (
              <p className="text-sm text-slate-400 py-8 text-center">
                No help article found for this feature.
              </p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
